import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeft, RefreshCw } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { BuscarMovimiento } from "@/components/documentos/buscar-movimiento";
import { DosPaneles } from "@/components/documentos/dos-paneles";
import { PapelDelExtracto } from "@/components/documentos/papel-del-extracto";
import {
  ElPagoDelExpediente,
  type Imagen,
  pagoDelExpediente,
  SeccionesDelExpediente,
} from "@/components/documentos/secciones-del-expediente";
import { EmptyState } from "@/components/feedback/empty-state";
import { type Papel, Visor } from "@/components/propuesta/visor";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";

/** Uno de los otros comprobantes que trae el mismo expediente. */
type OtroComprobante = {
  serieNumero: string | null;
  tipo: string;
  moneda: string;
  total: number | null;
  estado: string;
  anulaA: string | null;
  pagina: number | null;
};

export const Route = createFileRoute("/_app/archivo/$id")({ component: Ficha });

// ===========================================================================
// El expediente de un archivo: qué se hizo con él y qué salió.
//
// Responde la pregunta de contabilidad —«¿esto se leyó bien?»— que hasta ahora
// no tenía dónde responderse. Todo lo que se enseña acá llevaba guardado desde
// el principio en `paso.detalle`: 976 pasos sobre 305 archivos. Lo que faltaba
// era la pantalla.
// ===========================================================================

/** El punto de color de cada etapa en la tira. */
const PUNTO: Record<string, string> = {
  hecho: "bg-emerald-600",
  fallado: "bg-red-600",
  omitido: "bg-slate-400/60",
  en_curso: "bg-cyan-500",
  pendiente: "bg-muted-foreground/30",
};

const COMO_SE_DICE: Record<string, string> = {
  hecho: "Hecho",
  fallado: "Falló",
  omitido: "No aplica",
  en_curso: "En curso",
  pendiente: "Sin empezar",
};

function Ficha() {
  const { id } = Route.useParams();
  const { data, isPending } = api.fichaArchivo.useQuery(["archivo", id], {
    params: { id },
  });
  const d = data?.status === 200 ? data.body : null;
  const qc = useQueryClient();
  // El papel que se está mirando en grande: el PDF entero, hoja por hoja, o
  // uno de los recortes.
  const [papel, setPapel] = useState<Papel | null>(null);
  /** La imagen cuyo abono se está buscando en el banco. */
  const [buscando, setBuscando] = useState<Imagen | null>(null);
  /** La imagen que lleva el dinero: la que va arriba y suelta. */
  const pago = d?.expediente
    ? pagoDelExpediente(d.expediente.imagenes as Imagen[])
    : null;
  const reintentar = api.reintentarArchivo.useMutation();

  const dinero = (c: number | null) =>
    c === null
      ? "—"
      : (c / 100).toLocaleString("es-PE", { minimumFractionDigits: 2 });

  if (isPending) return <Skeleton className="h-96 w-full" />;
  // Un archivo que no está deja la pantalla sin nada que enseñar, así que hace
  // falta al menos una salida. Antes era un `<p>` suelto sin cabecera ni
  // enlace: quien llegaba desde un marcador viejo se quedaba mirando una línea
  // de texto gris en una página por lo demás en blanco.
  if (!d)
    return (
      <EmptyState
        title="Ese archivo no está"
        message="Puede que se haya descartado, o que el enlace sea de un periodo que ya se vació."
        action={
          <Button variant="outline" asChild>
            <Link to="/documentos/clientes">
              <ArrowLeft className="mr-1.5 size-4" />
              Ver los archivos del periodo
            </Link>
          </Button>
        }
      />
    );

  return (
    <>
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/documentos/clientes">
            <ArrowLeft className="mr-1.5 size-4" />
            Documentos
          </Link>
        </Button>
        <b
          className="truncate font-semibold text-[17px] tracking-tight"
          title={d.nombre}
        >
          {d.nombre.replace(/^.*\//, "")}
        </b>
        <span className="text-[12px] text-muted-foreground">
          {(d.bytes / 1024).toFixed(0)} KB · subido el {d.subidoEl}
        </span>
        {d.sePuedeReintentar ? (
          <Button
            size="sm"
            className="ml-auto"
            disabled={reintentar.isPending}
            onClick={() =>
              reintentar.mutate(
                { params: { id }, body: { confirmo: true } },
                {
                  onSuccess: (r: { body: { dice?: string } }) => {
                    toast.success(r.body.dice ?? "Encolado");
                    qc.invalidateQueries();
                  },
                  onError: () => toast.error("No se pudo reencolar"),
                },
              )
            }
          >
            <RefreshCw className="mr-1.5 size-3.5" />
            Volver a intentarlo
          </Button>
        ) : null}
      </div>

      {/* EL DOCUMENTO A LA DERECHA, Y TODO LO DEMÁS A LA IZQUIERDA.
          Pedido así: «eso debería verse más chiquito, en un cuadrito pegado a
          la izquierda, a la derecha ver todo el documento». Y tiene razón: se
          entra a esta ficha para mirar el papel, y el papel estaba detrás de un
          botón, después de seis tarjetas de pipeline y de once miniaturas.
          Cada lado se desplaza por dentro y el ancho se recuerda. */}
      <DosPaneles
        clave="archivo"
        soloIzquierda={!d.paginas || d.paginas < 1}
        izquierda={
          <div className="pr-1">
            {d.extracto ? (
              <div className="mt-5 rounded-lg border bg-card p-4">
                <h3 className="font-semibold text-[11px] text-muted-foreground uppercase tracking-wider">
                  Lo que trajo el extracto
                </h3>
                <div className="mt-2 grid gap-x-6 gap-y-1.5 text-[12.5px] sm:grid-cols-2 lg:grid-cols-3">
                  <Dato k="Banco" v={d.extracto.banco ?? "—"} />
                  <Dato k="Moneda" v={d.extracto.moneda ?? "—"} />
                  <Dato k="Cuenta" v={d.extracto.cuenta ?? "sin resolver"} />
                  <Dato k="Empresa" v={d.extracto.empresa ?? "—"} />
                  <Dato
                    k="Movimientos"
                    v={d.extracto.movimientos.toLocaleString("es-PE")}
                  />
                  <Dato k="Abonos" v={dinero(d.extracto.abonos)} />
                  <Dato k="Cargos" v={dinero(d.extracto.cargos)} />
                </div>
                {d.extracto.hallazgos.length > 0 ? (
                  <ul className="mt-2 space-y-1 text-[12px]">
                    {d.extracto.hallazgos.map(
                      (h: {
                        codigo: string;
                        gravedad: string;
                        mensaje: string;
                      }) => (
                        <li key={h.codigo + h.mensaje}>
                          <b>{h.gravedad === "error" ? "Error" : "Aviso"}:</b>{" "}
                          {h.mensaje}
                        </li>
                      ),
                    )}
                  </ul>
                ) : null}
              </div>
            ) : null}

            {/* EL PAGO, ARRIBA DEL TODO Y SUELTO.
                Estaba dentro del cajón de su clase —«La ficha del local 2», y
                dentro el cuadro—, y había que bajar por dos cabeceras de
                sección para llegar a lo único que se viene a mirar. Va aquí,
                antes que nada, con su botón de buscar el abono. */}
            {pago && d.expediente ? (
              <div className="mt-4">
                <ElPagoDelExpediente
                  imagen={pago}
                  boleta={d.expediente.serieNumero}
                  cliente={d.expediente.cliente}
                  alAbrirImagen={(i) =>
                    setPapel({
                      clase: "imagen",
                      recorte: i.recorte,
                      titulo: i.clase ?? "el pago del expediente",
                    })
                  }
                  alBuscar={() =>
                    setBuscando((antes) =>
                      antes?.recorte === pago.recorte ? null : pago,
                    )
                  }
                  panel={
                    buscando?.recorte === pago.recorte ? (
                      <BuscarMovimiento
                        voucher={{
                          importe: pago.importe,
                          moneda: pago.moneda,
                          operacion: pago.operacion,
                          fechaPago: pago.fechaPago,
                        }}
                        comprobanteId={d.expediente.comprobanteId}
                        totalBoleta={d.expediente.total}
                        alCerrar={() => setBuscando(null)}
                      />
                    ) : null
                  }
                />
              </div>
            ) : null}

            {d.expediente ? (
              <div className="mt-5 rounded-lg border bg-card p-4">
                <h3 className="font-semibold text-[11px] text-muted-foreground uppercase tracking-wider">
                  Lo que se extrajo del expediente
                </h3>
                <div className="mt-2 grid gap-x-6 gap-y-1.5 text-[12.5px] sm:grid-cols-2 lg:grid-cols-3">
                  <Dato k="Comprobante" v={d.expediente.serieNumero ?? "—"} />
                  <Dato k="Cliente" v={d.expediente.cliente ?? "—"} />
                  {/* El nombre del fichero dice otra persona. No se decide cuál vale
                    —eso lo mira alguien— pero deja de enseñarse en verde como si
                    los dos nombres fueran lo mismo. */}
                  {d.expediente.clienteDelNombre ? (
                    <Dato
                      k="Ojo · el fichero dice"
                      v={d.expediente.clienteDelNombre}
                      tono="aviso"
                    />
                  ) : null}
                  <Dato
                    k="Total"
                    v={`${d.expediente.moneda === "USD" ? "US$" : "S/"} ${dinero(d.expediente.total)}`}
                  />
                  <Dato
                    k="De dónde salió"
                    v={
                      d.expediente.origenLectura === "texto"
                        ? "del texto del PDF"
                        : (d.expediente.origenLectura ?? "—")
                    }
                  />
                </div>

                {/* LOS OTROS COMPROBANTES DEL MISMO EXPEDIENTE.
                  El caso corriente son tres papeles de la misma venta: la boleta
                  que salió mal, la nota de crédito que la anula y la que la
                  reemplaza. Enseñar solo uno hacía creer que era todo lo que había
                  dentro, y la nota —que RESTA— no aparecía en ningún sitio. */}
                {d.expediente.otrosComprobantes.length > 0 ? (
                  <div className="mt-3 rounded-md border border-amber-600/40 bg-amber-500/5 p-3">
                    <p className="font-semibold text-[11.5px] text-amber-700 dark:text-amber-500">
                      Este expediente trae{" "}
                      {d.expediente.otrosComprobantes.length + 1} comprobantes
                    </p>
                    <ul className="mt-1.5 space-y-1 text-[12px]">
                      {d.expediente.otrosComprobantes.map(
                        (c: OtroComprobante) => (
                          <li
                            key={c.serieNumero}
                            className="flex flex-wrap items-baseline gap-x-2"
                          >
                            <b className="font-medium">{c.serieNumero}</b>
                            <span className="text-muted-foreground">
                              {c.tipo === "nota_credito"
                                ? "nota de crédito"
                                : c.tipo === "nota_debito"
                                  ? "nota de débito"
                                  : c.tipo}
                            </span>
                            <span className="tabular-nums">
                              {c.moneda === "USD" ? "US$" : "S/"}{" "}
                              {dinero(c.total)}
                            </span>
                            {c.anulaA ? (
                              <span className="text-muted-foreground">
                                anula la {c.anulaA}
                              </span>
                            ) : null}
                            {c.estado === "anulado" ? (
                              <span className="rounded border border-amber-600/40 px-1.5 text-[11px] text-amber-700 dark:text-amber-500">
                                anulada
                              </span>
                            ) : null}
                            {c.pagina ? (
                              <span className="text-[11px] text-muted-foreground">
                                hoja {c.pagina}
                              </span>
                            ) : null}
                          </li>
                        ),
                      )}
                    </ul>
                  </div>
                ) : null}

                {/* LAS CUATRO PARTES DEL EXPEDIENTE.
                  Aquí colgaban las once imágenes en una rejilla, todas del mismo
                  tamaño y con su clase en letra pequeña debajo. Con cuatro DNI,
                  tres logos, la ficha y UN voucher dentro, eso no es un expediente
                  sino un montón — y así lo dijo contabilidad. Cada imagen ya traía
                  su clase desde la lectura; lo único que faltaba era ponerlas en su
                  cajón. */}
                <div className="mt-4">
                  <SeccionesDelExpediente
                    archivoId={d.id}
                    ficha={d.expediente.ficha}
                    imagenes={d.expediente.imagenes}
                    comprobante={{
                      serieNumero: d.expediente.serieNumero,
                      cliente: d.expediente.cliente,
                      total: d.expediente.total,
                      moneda: d.expediente.moneda,
                      pagina: d.expediente.pagina,
                    }}
                    otrosComprobantes={d.expediente.otrosComprobantes}
                    alBuscarMovimiento={(i) =>
                      setBuscando((antes) =>
                        antes?.recorte === i.recorte ? null : i,
                      )
                    }
                    buscando={buscando}
                    yaArriba={pago}
                    panel={
                      buscando ? (
                        <BuscarMovimiento
                          voucher={{
                            importe: buscando.importe,
                            moneda: buscando.moneda,
                            operacion: buscando.operacion,
                            fechaPago: buscando.fechaPago,
                          }}
                          comprobanteId={d.expediente?.comprobanteId ?? null}
                          totalBoleta={d.expediente?.total ?? null}
                          alCerrar={() => setBuscando(null)}
                        />
                      ) : null
                    }
                    alAbrirImagen={(i) =>
                      setPapel({
                        clase: "imagen",
                        recorte: i.recorte,
                        titulo: i.clase ?? "adjunto del expediente",
                      })
                    }
                    alAbrirHoja={(pagina) =>
                      setPapel({
                        clase: "expediente",
                        archivoId: d.id,
                        pagina,
                        paginas: d.paginas,
                        titulo: d.nombre,
                      })
                    }
                  />
                </div>
              </div>
            ) : null}
            {/* Y AL FINAL, CÓMO LLEGÓ HASTA AQUÍ.
        Estaba arriba y ocupaba la primera pantalla entera. Se mira cuando algo
        falló, no cada vez: lo que se viene a ver es el documento y lo que se
        le sacó. */}
            {/* LAS SEIS ETAPAS, EN UNA TIRA.
              Eran seis tarjetas a lo ancho de la pantalla, y ocupaban la primera
              pantalla entera antes de llegar a lo que se viene a ver: el documento.
              Contabilidad lo pidió «más chiquito, en un cuadrito pegado a la
              izquierda». Se sigue enseñando lo mismo —las seis, sus tiempos, sus
              intentos y su error entero— pero en el sitio que le toca. */}
            <div className="mt-4 rounded-lg border bg-card">
              <ul className="divide-y text-[12px]">
                {d.pasos.map((p: (typeof d.pasos)[number]) => (
                  <li key={p.etapa} className="px-3 py-1.5">
                    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                      <span
                        className={cn(
                          "size-2 shrink-0 translate-y-px rounded-full",
                          PUNTO[p.estado] ?? "bg-muted-foreground/40",
                        )}
                      />
                      <b className="w-[84px] shrink-0 font-medium capitalize">
                        {p.etapa}
                      </b>
                      <span className="text-[11.5px] text-muted-foreground">
                        {COMO_SE_DICE[p.estado] ?? p.estado}
                      </span>
                      {p.duro !== null ? (
                        <span className="text-[11px] text-muted-foreground tabular-nums">
                          {p.duro} s
                        </span>
                      ) : null}
                      {p.intentos > 1 ? (
                        <span className="text-[11px] text-amber-700 dark:text-amber-400">
                          {p.intentos} intentos
                        </span>
                      ) : null}
                    </div>
                    {p.dice.length > 0 ? (
                      <ul className="mt-0.5 flex flex-wrap gap-x-3 pl-[94px] text-[11px] text-muted-foreground">
                        {p.dice.map((x: string) => (
                          <li key={x}>{x}</li>
                        ))}
                      </ul>
                    ) : null}
                    {/* El error, entero. Es lo único que no se encoge: cuando algo
                      falla, el mensaje ES la pantalla. */}
                    {p.error ? (
                      <p className="mt-1 ml-[94px] rounded bg-red-600/10 px-2 py-1 font-mono text-[11px] leading-relaxed">
                        {p.error}
                      </p>
                    ) : null}
                  </li>
                ))}
              </ul>
            </div>

            <p className="mt-4 break-all text-[11px] text-muted-foreground">
              Huella del contenido:{" "}
              <span className="font-mono">{d.sha256}</span>
            </p>
          </div>
        }
        derecha={
          <PapelDelExtracto
            archivoId={d.id}
            paginas={d.paginas}
            titulo={
              d.clase === "estado_cuenta" ? "El PDF del banco" : "El expediente"
            }
            alAgrandar={(pagina) =>
              setPapel({
                clase: "expediente",
                archivoId: d.id,
                pagina,
                paginas: d.paginas,
                titulo: d.nombre,
              })
            }
          />
        }
      />

      <Visor papel={papel} alCerrar={() => setPapel(null)} />
    </>
  );
}

function Dato({
  k,
  v,
  tono,
}: {
  k: string;
  v: string;
  /** `aviso` para lo que se contradice con otro dato de la misma ficha. */
  tono?: "aviso";
}) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span
        className={cn(
          "text-muted-foreground",
          tono === "aviso" && "text-amber-700 dark:text-amber-500",
        )}
      >
        {k}
      </span>
      <b
        className={cn(
          "truncate text-right font-medium",
          tono === "aviso" && "text-amber-700 dark:text-amber-500",
        )}
        title={v}
      >
        {v}
      </b>
    </div>
  );
}
